// packages/web/src/components/CandidateDrawer.tsx
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, RotateCcw, Archive } from 'lucide-react';
import { useApplication, useForceReview, useArchiveApplication } from '../hooks/useApi';
import { FunnelStageBadge } from './FunnelStageBadge';
import { ScoreRing } from './ui/ScoreRing';

interface CandidateDrawerProps {
  applicationId: number | null;
  onClose: () => void;
}

function nameInitials(first?: string, last?: string): string {
  return `${(first ?? '')[0] ?? ''}${(last ?? '')[0] ?? ''}`.toUpperCase() || '?';
}

const AVATAR_COLORS = [
  'bg-sky-500', 'bg-violet-500', 'bg-amber-500',
  'bg-emerald-500', 'bg-rose-500', 'bg-indigo-500',
];

function avatarColor(name: string): string {
  let hash = 0;
  for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

const RECOMMENDATION_LABEL: Record<number, string> = {
  1: 'Poor', 2: 'Below Average', 3: 'Average', 4: 'Good', 5: 'Excellent',
};

export function CandidateDrawer({ applicationId, onClose }: CandidateDrawerProps) {
  const { data, isLoading } = useApplication(applicationId ?? 0);
  const forceReview = useForceReview();
  const archive = useArchiveApplication();

  // Close on Escape
  useEffect(() => {
    if (applicationId == null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [applicationId, onClose]);

  if (applicationId == null) return null;

  const candidate = data?.candidate;
  const latestReview = data?.reviews?.find(r => !r.archivedAt) ?? data?.reviews?.[0];
  const first = candidate?.firstName ?? data?.candidateFirstName ?? '';
  const last = candidate?.lastName ?? data?.candidateLastName ?? '';
  const name = `${first} ${last}`.trim() || 'Unknown';
  const color = avatarColor(name);
  const initials = nameInitials(first, last);
  const score = latestReview?.overallScore ?? data?.score;
  const tierLabel = latestReview ? (RECOMMENDATION_LABEL[latestReview.recommendation] ?? '') : '';

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="drawer-backdrop"
        className="fixed inset-0 bg-black/30 dark:bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-sm bg-white dark:bg-gray-900 shadow-xl z-50 flex flex-col border-l border-stone-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-stone-100 dark:border-gray-800">
          <span className="text-sm font-semibold text-stone-700 dark:text-stone-200">Candidate</span>
          <button
            aria-label="Close drawer"
            onClick={onClose}
            className="p-1 rounded hover:bg-stone-100 dark:hover:bg-gray-800 text-stone-400 dark:text-stone-500"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoading ? (
            <div className="space-y-3 animate-pulse">
              <div className="h-10 bg-stone-100 dark:bg-gray-800 rounded" />
              <div className="h-4 w-2/3 bg-stone-100 dark:bg-gray-800 rounded" />
              <div className="h-24 bg-stone-100 dark:bg-gray-800 rounded" />
            </div>
          ) : (
            <>
              {/* Identity */}
              <div className="flex items-start gap-3">
                <div className={`${color} w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-semibold text-sm`}>
                  {initials}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-stone-800 dark:text-stone-100 truncate">{name}</p>
                  {data?.job && (
                    <p className="text-sm text-stone-500 dark:text-stone-400 truncate">{data.job.title}</p>
                  )}
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <FunnelStageBadge stage={data?.srStage ?? ''} />
                    {tierLabel && (
                      <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded">{tierLabel}</span>
                    )}
                  </div>
                </div>
                {score != null && (
                  <div className="ml-auto flex-shrink-0 text-center">
                    <ScoreRing score={score} size="sm" />
                    <p className="text-[10px] text-stone-400 dark:text-stone-500 mt-0.5">of 50</p>
                  </div>
                )}
              </div>

              {/* Arbie's read */}
              {latestReview?.summary && (
                <div className="bg-stone-50 dark:bg-gray-800 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="text-[11px] font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide">Arbie's read</span>
                  </div>
                  <p className="text-sm text-stone-700 dark:text-stone-200 leading-relaxed">{latestReview.summary}</p>
                </div>
              )}

              {/* Score breakdown if available */}
              {latestReview?.strengths && (
                <div>
                  <p className="text-[11px] font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-1.5">Strengths</p>
                  <p className="text-sm text-stone-700 dark:text-stone-300">{latestReview.strengths}</p>
                </div>
              )}
              {latestReview?.concerns && (
                <div>
                  <p className="text-[11px] font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-1.5">Watch for</p>
                  <p className="text-sm text-stone-700 dark:text-stone-300">{latestReview.concerns}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-stone-100 dark:border-gray-800 space-y-3">
          <Link
            to={`/people/${data?.candidateId ?? ''}`}
            className="block w-full text-center text-sm text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300 font-medium"
          >
            View full application →
          </Link>
          <div className="flex gap-2">
            <button
              onClick={() => forceReview.mutate(applicationId)}
              disabled={forceReview.isPending}
              className="flex-1 flex items-center justify-center gap-1.5 text-sm border border-stone-300 dark:border-gray-600 text-stone-700 dark:text-stone-200 rounded-lg py-2 hover:bg-stone-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              <RotateCcw size={14} className={forceReview.isPending ? 'animate-spin' : ''} />
              Re-run
            </button>
            <button
              onClick={() => archive.mutate(applicationId)}
              disabled={archive.isPending}
              className="flex-1 flex items-center justify-center gap-1.5 text-sm border border-stone-300 dark:border-gray-600 text-stone-700 dark:text-stone-200 rounded-lg py-2 hover:bg-stone-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              <Archive size={14} />
              Archive
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
